import { FC, useContext, useState } from "react";
import { DocumentGetResponse } from "capitalrx-types";

import { FileUpload, Form, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Message, Messages, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Semibold } from "capitalrx-components-2.0/lib/typography";

import { useCreateAndAttachDocument } from "@/api";
import { CaseSearchForm } from "@/components";
import { ThemeSelectorContext } from "@/context";
import { getThemeButton } from "@/utils";


interface UploadDocumentFormFields {
  readonly file: File[];
}

interface DocumentUploadProps {
  caseParams: CaseSearchForm;
  handleUploadSuccess: (data: DocumentGetResponse) => void;
}

export const DocumentUpload: FC<DocumentUploadProps> = ({ caseParams, handleUploadSuccess }) => {
  const form = useForm<UploadDocumentFormFields>();
  const { file } = form.watch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { primary_color, primary_hover_color } = useContext(ThemeSelectorContext);

  const { mutateAsync: createAndAttachDocument } = useCreateAndAttachDocument();

  const handleDocumentUpload = async (formData: UploadDocumentFormFields) => {
    setIsSubmitting(true);
    const allowedExtensions = ["pdf", "tiff", "png"];
    const fileExtension = formData.file[0]?.name.split(".").pop()?.toLowerCase();

    if (fileExtension && !allowedExtensions.includes(fileExtension)) {
      setErrorMessage("File type isn't supported. Please upload a PDF, TIFF, or PNG file.");
      setIsSubmitting(false);
      return;
    }

    const createResponse = await createAndAttachDocument({
      case_id: caseParams.case_id,
      prescriber_npi: caseParams.prescriber_npi,
      external_member_id: caseParams.external_member_id,
      member_date_of_birth: caseParams.member_date_of_birth,
      file: formData.file,
    });

    if (createResponse) {
      handleUploadSuccess(createResponse);
      form.reset();
      setErrorMessage(null);
    };
    setIsSubmitting(false);
  };

  return (
    <Form form={form} onSubmit={handleDocumentUpload} testId="document-upload-form">
      <Semibold style={{ fontSize: "1rem" }}>Upload Supporting Document</Semibold>
      <Grid spacing="large">
        <Grid.Span columns={12}>
          <FileUpload.Field
            maxSize={100000000}
            allowedExtensions={["pdf", "tiff", "png"]}
            name="file"
            required
            testId="document-upload-file-upload"
            disabled={isSubmitting}
          />
        </Grid.Span>
        <Grid.Break />
        {isSubmitting && <Stack alignment="center"><Spinner /></Stack>}
        {!!file
          && <Shelf spacing="small">
            <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)} testId="document-upload-submit">Upload</SubmitButton>
            <Shelf.Spacer />
          </Shelf>
        }
        {errorMessage && (
          <Message appearance={Messages.Error} visible>
            {errorMessage}
          </Message>
        )}
      </Grid>
    </Form>
  );
};
